import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { SEO_SERVICES_BY_SLUG } from './data/seoServices';
import { FOCUSED_PAGES, SEO_ALIASES } from './data/focusedLanding';
import { COUNTRY_MARKETS } from './data/countryMarkets';
import { getLeadSource, setLeadSource } from './utils/leadSource';
import { AutoBreadcrumbs } from './components/AutoBreadcrumbs';
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { ChatWidget } from './components/ChatWidget';
import { ProtectedRoute } from './components/ProtectedRoute';
import { trackPageView } from './utils/analytics';

// Pages
import Home from './pages/Home';
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const GetStarted = lazy(() => import('./pages/GetStarted'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Security = lazy(() => import('./pages/Security'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const ClientDashboard = lazy(() => import('./pages/ClientDashboard'));
const ClientLogin = lazy(() => import('./pages/ClientLogin'));
const SampleRequest = lazy(() => import('./pages/SampleRequest'));
const QuoteCalculator = lazy(() => import('./pages/QuoteCalculator'));
const ServiceAreas = lazy(() => import('./pages/ServiceAreas'));
const StateCoverage = lazy(() => import('./pages/StateCoverage'));
const CountyCoverage = lazy(() => import('./pages/CountyCoverage'));
const PaymentResult = lazy(() => import('./pages/PaymentResult'));
const Support = lazy(() => import('./pages/Support'));
const CountryMarket = lazy(() => import('./pages/CountryMarket'));
const LeadSources = lazy(() => import('./pages/admin/LeadSources'));
const SeoPages = lazy(() => import('./pages/admin/SeoPages'));
const QuotesAgreements = lazy(() => import('./pages/admin/QuotesAgreements'));
const CaseStudiesAdmin = lazy(() => import('./pages/admin/CaseStudiesAdmin'));
const BlogAdmin = lazy(() => import('./pages/admin/BlogAdmin'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Careers = lazy(() => import('./pages/Careers'));
const FocusedLanding = lazy(() => import('./pages/FocusedLanding'));
const ServiceVertical = lazy(() => import('./pages/ServiceVertical'));
const StateService = lazy(() => import('./pages/StateService'));

// Admin Panel
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Overview = lazy(() => import('./pages/admin/Overview'));
const WebsiteLeads = lazy(() => import('./pages/admin/WebsiteLeads'));
const LeadFinder = lazy(() => import('./pages/admin/LeadFinder'));
const AdminContacts = lazy(() => import('./pages/admin/Contacts'));
const Templates = lazy(() => import('./pages/admin/Templates'));
const Campaigns = lazy(() => import('./pages/admin/Campaigns'));
const ActivityFeed = lazy(() => import('./pages/admin/ActivityFeed'));
const SystemHealth = lazy(() => import('./pages/admin/SystemHealth'));

function AdminIndex() {
  const { hash } = useLocation();
  return <Navigate to={`/admin/${hash.slice(1) || 'overview'}`} replace />;
}

function ServicesRouter() {
  const { serviceType } = useParams();
  return SEO_SERVICES_BY_SLUG[serviceType] ? <ServiceVertical /> : <Services />;
}

function GeoRouter() {
  const { state, second } = useParams();
  return SEO_SERVICES_BY_SLUG[second] ? <StateService /> : <Navigate to={`/service-areas/${state}/county/${second}`} replace />;
}

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main id="main-content">
        <AutoBreadcrumbs />
        {children}
      </main>
      <Footer />
      <ChatWidget />
    </>
  );
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, document.title);
    if (!getLeadSource() && !location.pathname.startsWith('/admin')) setLeadSource(`page:${location.pathname}`);
  }, [location]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <PageTracker />
            <Suspense fallback={<div className="min-h-screen" data-testid="route-loading" />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
              <Route path="/services/:serviceType" element={<PublicLayout><ServicesRouter /></PublicLayout>} />
              <Route path="/services/:serviceType/:sub" element={<PublicLayout><ServiceVertical /></PublicLayout>} />
              {FOCUSED_PAGES.map(p => <Route key={p.slug} path={`/${p.slug}`} element={<PublicLayout><FocusedLanding page={p} /></PublicLayout>} />)}
              {Object.entries(SEO_ALIASES).map(([from, to]) => <Route key={from} path={`/${from}`} element={<Navigate to={to} replace />} />)}
              <Route path="/get-started" element={<PublicLayout><GetStarted /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
              <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
              <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
              <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
              <Route path="/pricing" element={<PublicLayout><Pricing /></PublicLayout>} />
              <Route path="/portfolio" element={<PublicLayout><Portfolio /></PublicLayout>} />
              <Route path="/samples" element={<PublicLayout><SampleRequest /></PublicLayout>} />
              <Route path="/quote" element={<PublicLayout><QuoteCalculator /></PublicLayout>} />
              <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />
              <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
              <Route path="/security" element={<PublicLayout><Security /></PublicLayout>} />
              <Route path="/service-areas" element={<PublicLayout><ServiceAreas /></PublicLayout>} />
              <Route path="/service-areas/:state" element={<PublicLayout><StateCoverage /></PublicLayout>} />
              <Route path="/service-areas/:state/:second" element={<PublicLayout><GeoRouter /></PublicLayout>} />
              <Route path="/service-areas/:state/county/:county" element={<PublicLayout><CountyCoverage /></PublicLayout>} />
              <Route path="/payment/success" element={<PublicLayout><PaymentResult /></PublicLayout>} />
              <Route path="/payment/cancel" element={<PublicLayout><PaymentResult cancelled /></PublicLayout>} />
              {COUNTRY_MARKETS.map(m => <Route key={m.slug} path={`/${m.slug}`} element={<PublicLayout><CountryMarket market={m} /></PublicLayout>} />)}
              {COUNTRY_MARKETS.filter(m => m.code !== 'us').map(m => <Route key={`${m.slug}-item`} path={`/${m.slug}/:item`} element={<PublicLayout><CountryMarket market={m} /></PublicLayout>} />)}
              <Route path="/canada/montreal/fr" element={<PublicLayout><CountryMarket market={COUNTRY_MARKETS.find(m => m.code === 'ca')} fr /></PublicLayout>} />
              <Route path="/careers" element={<PublicLayout><Careers /></PublicLayout>} />
              <Route path="/support" element={<PublicLayout><Support /></PublicLayout>} />
              
              {/* Client Auth & Dashboard */}
              <Route path="/login" element={<ClientLogin />} />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <ClientDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route 
                path="/admin"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminIndex />} />
                <Route path="overview" element={<Overview />} />
                <Route path="projects" element={<AdminDashboard embedded />} />
                <Route path="quotes" element={<QuotesAgreements />} />
                <Route path="lead-sources" element={<LeadSources />} />
                <Route path="seo-pages" element={<SeoPages />} />
                <Route path="case-studies" element={<CaseStudiesAdmin />} />
                <Route path="blog" element={<BlogAdmin />} />
                <Route path="leads" element={<WebsiteLeads />} />
                <Route path="lead-finder" element={<LeadFinder />} />
                <Route path="contacts" element={<AdminContacts />} />
                <Route path="templates" element={<Templates />} />
                <Route path="campaigns" element={<Campaigns />} />
                <Route path="activity" element={<ActivityFeed />} />
                <Route path="health" element={<SystemHealth />} />
                <Route path="dashboard" element={<Navigate to="/admin/projects" replace />} />
                <Route path="email-campaign" element={<Navigate to="/admin/campaigns" replace />} />
              </Route>
              <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
